import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import * as flashActions from 'redux-flash'
import { onError } from 'lp-hoc'
import { logException } from 'sentry'
import {
  Content,
  FlashMessageContainer,
  SessionExpiryMonitor,
  ExternalLink,
  AccountMenu,
  NotificationsMenu,
  Modal,
  RenderedHtml,
  PortalSelector,
  FooterLegalese,
} from 'components'
import * as apiActions from 'api-actions'
import { replace } from 'react-router-redux'
import { selectors as globalSelectors } from 'global-reducer'
import { selectors as globalSchoolSelectors } from 'school-portal-reducer'
import { selectors as surveySelectors } from './survey/reducer'
import {
  updateDocumentTitle,
  useCommunity,
  useCommunityText,
  isEducatorEnabled,
  getCurrentEmployment,
  displaySubmitFailure,
  distinctEmploymentPositions,
  initializeAuthenticatedUserflow,
} from 'utils'
import isLearningHubEnabled from 'utils/local/is-learning-hub-enabled'
import PortalPrimaryNav from './portal-primary-nav'
import { Spinner } from 'lp-components'
import { SCHOOL_PORTAL_ROUTE, EDUCATOR_PORTAL_ROUTE } from 'config'
import { kebabCase } from 'lodash'
import classnames from 'classnames'
import { NotificationsPreferencesForm } from 'forms'
import menuIcon from 'images/menu.svg'

const propTypes = {
  currentSchoolId: PropTypes.number.isRequired,
  children: PropTypes.node,
  district: Types.district,
  fetchActiveSchoolYears: PropTypes.func.isRequired,
  fetchSchool: PropTypes.func.isRequired,
  fetchCompletionSchool: PropTypes.func.isRequired,
  fetchNotifications: PropTypes.func.isRequired,
  markAllAsRead: PropTypes.func.isRequired,
  fetchSurveyAttempt: PropTypes.func.isRequired,
  fetchUserDetails: PropTypes.func.isRequired,
  flashMessages: PropTypes.arrayOf(flashActions.flashMessageType),
  notifications: PropTypes.arrayOf(Types.notification),
  isAdmin: PropTypes.bool,
  isSurveyOpen: PropTypes.bool.isRequired,
  surveySchoolYear: Types.schoolYear,
  redirectToTerms: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  userDetails: Types.user,
  redirectToSignOut: PropTypes.func.isRequired,
  redirectToEducator: PropTypes.func.isRequired,
  currentSchoolYear: Types.schoolYear.isRequired,
  fetchNotificationPreferences: PropTypes.func.isRequired,
  updateNotificationPreferences: PropTypes.func.isRequired,
  notificationPreferences: Types.notificationPreferences,
  school: PropTypes.object,
}

const defaultProps = {
  children: null,
  flashMessages: [],
  notifications: [],
  isAdmin: false,
  district: null,
  surveySchoolYear: null,
  userDetails: null,
}

function LayoutAuthorized({
  children,
  currentSchoolId,
  district,
  flashMessages,
  isAdmin,
  notifications,
  isSurveyOpen,
  fetchActiveSchoolYears,
  fetchNotifications,
  markAllAsRead,
  fetchSchool,
  fetchCompletionSchool,
  fetchSurveyAttempt,
  fetchUserDetails,
  surveySchoolYear,
  userDetails,
  redirectToTerms,
  location,
  redirectToSignOut,
  redirectToEducator,
  currentSchoolYear,
  fetchNotificationPreferences,
  notificationPreferences,
  updateNotificationPreferences,
  school,
}) {
  const community = useCommunity()
  const t = useCommunityText()
  const terms = t('terms')
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [showPreferencesModal, setShowPreferencesModal] = useState(false)
  const [holdForRedirect, setHoldForRedirect] = useState(!!terms)
  const isSignOutPath = location.pathname.includes('sign-out')
  const showNotificationsMenu = community.notificationsEnabled

  useEffect(() => {
    if (isSignOutPath) return
    fetchSchool(currentSchoolId)
  }, [currentSchoolId, isSignOutPath, fetchSchool])

  useEffect(() => {
    if (isSignOutPath || !surveySchoolYear) return
    fetchSchool(currentSchoolId, surveySchoolYear.id)
  }, [currentSchoolId, surveySchoolYear?.id, isSignOutPath])

  // Load the current-school-year school used exclusively for profile completion,
  // so the completion bar is always evaluated against the current year's data
  // regardless of which year the global school is loaded for.
  useEffect(() => {
    if (isSignOutPath || !currentSchoolYear) return
    fetchCompletionSchool(currentSchoolId, currentSchoolYear.id)
  }, [currentSchoolId, currentSchoolYear?.id, isSignOutPath])

  useEffect(() => {
    if (isSignOutPath) return
    fetchNotifications()
  }, [isSignOutPath, fetchNotifications])

  useEffect(() => {
    if (isSignOutPath) return
    if (userDetails?.notificationSubscriptionId) {
      fetchNotificationPreferences(userDetails.notificationSubscriptionId)
    }
  }, [
    userDetails?.notificationSubscriptionId,
    isSignOutPath,
    fetchNotificationPreferences,
  ])

  useEffect(() => {
    if (isSignOutPath) return
    fetchSurveyAttempt(currentSchoolId)
  }, [currentSchoolId, isSignOutPath, fetchSurveyAttempt])

  useEffect(() => {
    if (isSignOutPath) return
    fetchActiveSchoolYears({ schoolId: currentSchoolId })
  }, [currentSchoolId, isSignOutPath, fetchActiveSchoolYears])

  useEffect(() => {
    if (isSignOutPath) return
    if (!userDetails) fetchUserDetails()
  }, [isSignOutPath, userDetails, fetchUserDetails])

  const educatorEnabled = useMemo(
    () => isEducatorEnabled(community),
    [community]
  )
  const learningHubAccess = isLearningHubEnabled(userDetails, school)

  useEffect(() => {
    if (terms && userDetails) {
      if (!userDetails.agreedAt && !location.pathname.includes('sign-out')) {
        redirectToTerms()
      } else {
        setHoldForRedirect(false)
      }
    }
  }, [userDetails, terms])

  const currentEmployment = useMemo(() => {
    if (!userDetails) return
    return getCurrentEmployment({
      employments: userDetails.employments,
      currentEmployableId: currentSchoolId,
      currentEmployableType: Types.EMPLOYABLE_TYPES.SCHOOL,
    })
  }, [userDetails, currentSchoolId])

  const employmentPositions = userDetails
    ? distinctEmploymentPositions(userDetails.employments)
    : []

  const hasLoaded = !!userDetails

  // if a current employment wasn't found, the user is not correctly signed in
  if (hasLoaded && !currentEmployment) redirectToSignOut()
  // If the current employment is a teacher redirect them to aE as they don't have access to the School portal
  if (
    educatorEnabled &&
    currentEmployment &&
    currentEmployment.position === Types.POSITION_TYPES.TEACHER
  )
    redirectToEducator()

  initializeAuthenticatedUserflow(userDetails)

  const [navVisibleOnMobile, setNavVisibleOnMobile] = useState(false)
  const closeMobileNav = () => {
    setNavVisibleOnMobile(false)
    return true
  }

  return (
    <div className={kebabCase(Types.PORTAL.SCHOOL_PORTAL)}>
      <a href="#content" id="skip-nav-link">
        Skip to Main Content
      </a>
      <button
        type="button"
        className="nav-toggle"
        aria-expanded={navVisibleOnMobile}
        aria-controls="side-navigation-content"
        onClick={() => setNavVisibleOnMobile(true)}
        aria-label="Open Navigation"
      >
        <img src={menuIcon} alt="" />
      </button>
      <div
        id="side-navigation-content"
        className={classnames('nav-container', {
          'is-active': navVisibleOnMobile,
        })}
      >
        <button
          type="button"
          className="close"
          aria-label="Close Navigation"
          onClick={closeMobileNav}
        >
          ×
        </button>
        {(educatorEnabled || learningHubAccess) && (
          <PortalSelector
            currentPortal={Types.PORTAL.SCHOOL_PORTAL}
            currentEmployment={currentEmployment}
            learningHubAccess={learningHubAccess}
          />
        )}
        <PortalPrimaryNav
          community={community}
          district={district}
          isSurveyOpen={isSurveyOpen}
          surveySchoolYear={surveySchoolYear}
          currentSchoolYear={currentSchoolYear}
          closeMobileNav={closeMobileNav}
        />
      </div>
      {!hasLoaded || holdForRedirect ? (
        <Spinner />
      ) : (
        <div
          className={classnames({
            'portal-select-enabled': educatorEnabled || learningHubAccess,
          })}
        >
          {isAdmin && (
            <div className="flash-message admin failure">
              <p>You are viewing the portal as an admin</p>
            </div>
          )}
          <FlashMessageContainer messages={flashMessages} />
          <SessionExpiryMonitor redirectToSignOut={redirectToSignOut} />
          <Content id="content" className="portal-wrapper">
            {showNotificationsMenu && notificationPreferences && (
              <NotificationsMenu
                openPreferencesModal={(show) => setShowPreferencesModal(show)}
                notifications={notifications}
                markAllAsRead={markAllAsRead}
              />
            )}
            <AccountMenu
              currentEmployment={currentEmployment}
              currentEmployableType={Types.EMPLOYABLE_TYPES.SCHOOL}
              employments={userDetails.employments}
              userProfile={userDetails.profile}
              userEmail={userDetails.email}
            />
            {children}
            <footer className="portal-footer">
              <FooterLegalese
                hasTerms={!!terms}
                openTermsModal={() => setShowTermsModal(true)}
              />
              <p className="contact">
                Email{' '}
                <ExternalLink href={`mailto:${community.schoolSupportEmail}`}>
                  {community.schoolSupportEmail}
                </ExternalLink>{' '}
                with questions.
              </p>
            </footer>
            {showTermsModal && (
              <Modal onClose={() => setShowTermsModal(false)}>
                <h2>Terms & Conditions</h2>
                <div className="modal-content">
                  <RenderedHtml>{terms}</RenderedHtml>
                </div>
              </Modal>
            )}
            {showPreferencesModal && (
              <Modal onClose={() => setShowPreferencesModal(false)}>
                <NotificationsPreferencesForm
                  employmentPositions={employmentPositions}
                  initialValues={notificationPreferences}
                  onSubmit={(params) =>
                    updateNotificationPreferences(
                      notificationPreferences.id,
                      params
                    )
                  }
                  onSubmitSuccess={() => setShowPreferencesModal(false)}
                  onSubmitFail={displaySubmitFailure}
                  closePreferencesModal={() => setShowPreferencesModal(false)}
                />
              </Modal>
            )}
          </Content>
        </div>
      )}
    </div>
  )
}

LayoutAuthorized.propTypes = propTypes
LayoutAuthorized.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    currentSchoolId: globalSchoolSelectors.currentSchoolId(state),
    district: globalSchoolSelectors.district(state),
    flashMessages: flashActions.getFlashMessages(state),
    isAdmin: globalSchoolSelectors.isAdmin(state),
    isSurveyOpen: surveySelectors.isSurveyOpen(state),
    school: globalSchoolSelectors.school(state),
    notifications: globalSchoolSelectors.notifications(state),
    surveySchoolYear: globalSchoolSelectors.surveySchoolYear(state),
    userDetails: globalSchoolSelectors.userDetails(state),
    currentSchoolYear: globalSelectors.currentSchoolYear(state),
    notificationPreferences:
      globalSchoolSelectors.notificationPreferences(state),
  }
}

const mapDispatchToProps = {
  fetchActiveSchoolYears: apiActions.fetchActiveSchoolYears,
  fetchSchool: apiActions.fetchSchoolAuthorized,
  fetchCompletionSchool: apiActions.fetchCompletionSchool,
  fetchNotifications: apiActions.fetchNotifications,
  fetchNotificationPreferences: apiActions.fetchNotificationPreferences,
  updateNotificationPreferences: apiActions.updateNotificationPreferences,
  fetchSurveyAttempt: apiActions.fetchSurveyAttempt,
  fetchUserDetails: apiActions.fetchCurrentSchoolUserDetails,
  markAllAsRead: apiActions.markAllAsRead,
  redirectToTerms: () => replace(SCHOOL_PORTAL_ROUTE + '/terms'),
  redirectToSignOut: () => replace(SCHOOL_PORTAL_ROUTE + '/sign-out'),
  redirectToEducator: () => replace(EDUCATOR_PORTAL_ROUTE),
}

function updateTitle({ school }) {
  const title = school ? `${school.name} - ` : ''
  return `${title}Arts Education | artlook`
}

function onComponentDidCatch(_, error, errorInfo) {
  return logException(error, errorInfo)
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  updateDocumentTitle(updateTitle),
  onError(onComponentDidCatch)
)(LayoutAuthorized)
